import { useEffect, useMemo, useState } from "react"
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { formatWorkingDays } from "../format-utils"
import { CostBreakdownRow } from "./cost-breakdown-row"
import { CostFigure } from "./cost-figure"

const DEFAULT_ROWS_PER_PAGE = 100
const ROWS_PER_PAGE_OPTIONS = [50, 100, 250]

export function CostBreakdownTable({
    rows,
    config,
    totals,
    costResult,
    currencySymbol,
    showBillingAdjustment,
    isFiltered,
    // When present, pagination is CONTROLLED by the server: `rows` is already one
    // page and the caller owns page/rowsPerPage state and data fetching.
    serverPagination,
}) {
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
    const { hoursPerDay } = useBoundContext()
    const currentPage = serverPagination ? serverPagination.page : getCurrentPage()
    const effectiveRowsPerPage = serverPagination ? serverPagination.rowsPerPage : rowsPerPage
    const visibleRows = useMemo(getVisibleRows, [currentPage, rows, rowsPerPage, serverPagination])
    const paginationCount = serverPagination ? serverPagination.count : rows.length
    const showPagination = paginationCount > effectiveRowsPerPage
    const timeLabel = getTimeLabel()
    const subtotalCost = getSubtotalCost()

    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the row set changes; serverPagination owns its own page state
    useEffect(resetPage, [rows])

    return (
        <If value={rows.length === 0}>
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                {config.emptyMessage}
            </Typography>
            <Else>
                <TableContainer>
                    <Table
                        sx={{
                            "& .MuiTableHead-root .MuiTableCell-root": {
                                fontSize: 16,
                                py: 1.5,
                            },
                            "& .MuiTableBody-root .MuiTableCell-root": {
                                fontSize: 16,
                                py: 1.5,
                            },
                            "& .MuiTypography-root": {
                                fontSize: 16,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 220 }}>{config.primaryColumnLabel}</TableCell>
                                <If value={config.showTaskCount}>
                                    <TableCell align="right" sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                                        Tasks
                                    </TableCell>
                                </If>
                                <If value={showBillingAdjustment}>
                                    <TableCell align="right" sx={{ minWidth: 140, whiteSpace: "nowrap" }}>
                                        Actual Time
                                    </TableCell>
                                </If>
                                <TableCell align="right" sx={{ minWidth: 140, whiteSpace: "nowrap" }}>
                                    {timeLabel}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 140, whiteSpace: "nowrap" }}>
                                    PPM Cost
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat
                                list={visibleRows}
                                keyFn="id"
                                item={
                                    <CostBreakdownRow
                                        currencySymbol={currencySymbol}
                                        showBillingAdjustment={showBillingAdjustment}
                                        showTaskCount={config.showTaskCount}
                                    />
                                }
                            />
                            <TableRow sx={{ bgcolor: "action.hover" }} data-cy="cost-resource-subtotal-row">
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {config.totalLabel}
                                    </Typography>
                                </TableCell>
                                <If value={config.showTaskCount}>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={600}>
                                            {totals.taskCount}
                                        </Typography>
                                    </TableCell>
                                </If>
                                <If value={showBillingAdjustment}>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatWorkingDays(totals.actualHours, hoursPerDay)}
                                        </Typography>
                                    </TableCell>
                                </If>
                                <TableCell align="right">
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatWorkingDays(totals.totalHours, hoursPerDay)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <CostFigure value={subtotalCost} currencySymbol={currencySymbol} fontWeight={600} />
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ borderTop: 2, borderColor: "divider" }} data-cy="cost-resource-total-row">
                                <TableCell>
                                    <Typography variant="subtitle2">Total</Typography>
                                </TableCell>
                                <If value={config.showTaskCount}>
                                    <TableCell />
                                </If>
                                <If value={showBillingAdjustment}>
                                    <TableCell />
                                </If>
                                <TableCell />
                                <TableCell align="right">
                                    <CostFigure
                                        value={costResult?.grandTotal}
                                        currencySymbol={currencySymbol}
                                        variant="subtitle2"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <If value={showPagination}>
                    <TablePagination
                        component="div"
                        count={paginationCount}
                        page={currentPage}
                        rowsPerPage={effectiveRowsPerPage}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        nextIconButtonProps={getNextButtonProps()}
                        backIconButtonProps={getBackButtonProps()}
                    />
                </If>
            </Else>
        </If>
    )

    function getVisibleRows() {
        if (serverPagination) return rows
        const start = currentPage * rowsPerPage
        return rows.slice(start, start + rowsPerPage)
    }

    function getCurrentPage() {
        if (!rows.length) return 0
        return Math.min(page, Math.ceil(rows.length / rowsPerPage) - 1)
    }

    function resetPage() {
        if (!serverPagination) setPage(0)
    }

    function handlePageChange(_event, nextPage) {
        if (serverPagination) {
            serverPagination.onPageChange(nextPage)
            return
        }
        setPage(nextPage)
    }

    function handleRowsPerPageChange(event) {
        const nextRowsPerPage = parseInt(event.target.value, 10)
        if (serverPagination) {
            serverPagination.onRowsPerPageChange(nextRowsPerPage)
            return
        }
        setRowsPerPage(nextRowsPerPage)
        setPage(0)
    }

    // Keyset pagination walks one page at a time: hold navigation while a page is
    // loading, and forwards only when the server reported another page.
    function getNextButtonProps() {
        if (!serverPagination) return { "data-cy": "cost-breakdown-next-page" }
        return {
            "data-cy": "cost-breakdown-next-page",
            disabled: serverPagination.loading || !serverPagination.hasNextPage,
        }
    }

    function getBackButtonProps() {
        if (!serverPagination) return { "data-cy": "cost-breakdown-previous-page" }
        return {
            "data-cy": "cost-breakdown-previous-page",
            disabled: serverPagination.loading || currentPage === 0,
        }
    }

    function getTimeLabel() {
        if (showBillingAdjustment) return "Billed Time"
        return "Time"
    }

    function getSubtotalCost() {
        if (isFiltered) return totals.totalCost
        if (config.useMaintenanceSubtotal) return costResult?.maintenanceSubtotal
        return totals.totalCost
    }
}
